"use client";
import {
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
  Logo,
  TextField,
} from "@/components";
import { useCompanyInfoInquiryMutation } from "@/hooks/useInquiry";
import {
  IServiceFormProps,
  TCompanyIdentificationNumber,
  TRawServiceApiKeys,
} from "@/interfaces";
import { cn } from "@/lib";
import { useAppStore, useModalStore, useSecureStore } from "@/store";
import { companyIdentificationNumberSchema } from "@/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import InquiryCost from "../../inquiryCost";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { legalServicesQuickAccessNavigation } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import React, { useEffect, useState } from "react";
import HtmlRenderer from "../../htmlRenderer";
import MainLogo from "@public/svg/logo-2.svg";
import Loader from "@/components/ui/loader";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { ICompanyInfoInquiryMutationPayload } from "@/interfaces/inquiries/legal";
import SampleInquiryModal from "@/components/inquirySamples/sampleInquiryModal";
import CompanyInfoSample from "@/components/inquirySamples/companyInfoSample";

const CompanyInformationInquiryForm = ({
  cost,
  isLoggedIn,
  navigateToLogin,
  announcement,
  disableService,
  serviceId,
}: IServiceFormProps) => {
  const { data: session } = useSession();
  const { persistedFields } = useSecureStore();
  const { setPostLoginAction } = useAppStore();
  const {
    isPending: isSubmitting,
    mutate: companyInfoInquiryFn,
    error: companyInfoInquiryError,
    reset: resetCompanyInfoInquiry,
  } = useCompanyInfoInquiryMutation(session?.user.token);
  const router = useRouter();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const isPaymentMethodModalOpen = openModal === "payment-method";
  const openPaymentModal = () => {
    setOpenModal("payment-method");
  };

  const serviceKey = TRawServiceApiKeys.Company_Information_Inquiry;

  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
  } = useForm<TCompanyIdentificationNumber>({
    resolver: zodResolver(companyIdentificationNumberSchema),
    defaultValues: {
      digit: persistedFields?.[serviceKey]?.digit || "",
    },
  });

  const [packageId, setPackageId] = useState<string | null>(null);
  // Handle package selection
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };

  const [isSampleOpen, setIsSampleOpen] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (data: any) => {
    setError(null);
    if (!isLoggedIn) {
      setPostLoginAction("open-payment-modal");
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };

  const paymentSubmission = () => {
    const formData = getValues();

    const payload: Readonly<ICompanyInfoInquiryMutationPayload> = {
      national_code: formData.digit,
      campaign: packageId || undefined,
    };
    companyInfoInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (error) => {
          if (error) {
            setError(error.message);
            setIsTimerActive(true); // Start timer
            setTimerEndTime(Date.now() + 60000);
          }
        },
      },
    );
  };

  const t = useTranslations("serviceFormSubmissions");

  const pathname = usePathname();

  const activeKey = legalServicesQuickAccessNavigation.find(
    (item) =>
      item.key === globalRawServicesData.Company_Information_Inquiry.key,
  )!;

  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("payment") === "1") {
      setOpenModal("payment-method");
    }
  }, [searchParams, setOpenModal]);

  useEffect(() => {
    if (isPaymentMethodModalOpen) {
      resetCompanyInfoInquiry();
    }
  }, [isPaymentMethodModalOpen]);

  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBetweenCol gap={8} className={cn("w-full")}>
          <div className="mb-8 w-full max-w-[80%]">
            <Controller
              name={"digit"}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  maxLength={11}
                  numeric
                  hasShadow
                  aria-label="شماره شناسایی"
                  inputMode="numeric"
                  placeholder={t("companyInformationInquiry.placeholder")}
                  inputClassName={cn("tracking-widest h-10")}
                  error={formError.digit?.message}
                />
              )}
            />
          </div>
          {session?.user.token && <InquiryCost cost={cost} />}
          {announcement && <HtmlRenderer htmlString={announcement} />}
          <FlexBetween gap={2} className="lg:gap-4">
            {/*<CustomButton*/}
            {/*  id="company-information-inquiry-sample"*/}
            {/*  type="button"*/}
            {/*  className="border-primary-main text-primary-main hover:bg-primary-main/5 border bg-transparent !px-3 !text-sm"*/}
            {/*  onClick={() => setIsSampleOpen(true)}*/}
            {/*>*/}
            {/*  نمونه نتیجه*/}
            {/*</CustomButton>*/}
            <CustomButton
              id="company-information-inquiry-submit"
              disabled={disableService}
              className="flex-1"
              variant="primary"
              type="submit"
            >
              {t("companyInformationInquiry.confirmButton")}
            </CustomButton>
          </FlexBetween>
        </FlexBetweenCol>
      </form>
      {isSampleOpen && (
        <SampleInquiryModal
          onClose={() => setIsSampleOpen(false)}
          title="نمونه نتیجه استعلام اطلاعات شرکت"
        >
          <CompanyInfoSample />
        </SampleInquiryModal>
      )}
      {isPaymentMethodModalOpen && (
        <PaymentModalContainer
          payBtnId="payment-company-information-inquiry"
          submissionError={companyInfoInquiryError}
          submissionLoading={isSubmitting}
          paymentSubmission={paymentSubmission}
          isOpen={isPaymentMethodModalOpen}
          onClose={closeModal}
          selectedService={activeKey}
          onErrorReset={resetCompanyInfoInquiry}
          onPackageSelect={handlePackageSelect}
          serviceId={serviceId}
          showData="service"
          persistedData={{
            [serviceKey]: {
              ...getValues(),
            },
          }}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
      {isSubmitting && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <Logo className="mb-3" src={MainLogo} width={60} height={60} />
            <h2 className="text-center text-sm font-semibold sm:text-base">
              درحال استعلام <br /> لطفا شکیبا باشید
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyInformationInquiryForm;
